import type { UseFernieRequestsReturn } from "~/hooks/useFernieRequests";
import type { FernieRequestStatus } from "~/services/aiOrchestrator";

import { Box, Chip, CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";

import RetryableErrorState from "~/components/RetryableErrorState";
import { BodyText, CaptionText, TitleText } from "~/components/Typographies";
import useLocaleFormat from "~/hooks/useLocaleFormat";
import { TOUR_ANCHORS } from "~/walkthroughs/registry";

interface FernieRequestsListProps {
	requests: UseFernieRequestsReturn;
}

const statusColor: Record<FernieRequestStatus, "default" | "info" | "success"> = {
	BEING_BUILT: "info",
	CLOSED: "default",
	DONE: "success",
	IN_NEXT_UPDATE: "info",
	IN_TESTING: "info",
	NOT_PLANNED: "default",
	PLANNED: "info",
	RECEIVED: "default"
};

const FernieRequestsContent = ({ requests }: FernieRequestsListProps) => {
	const { t } = useTranslation("common");
	const fmt = useLocaleFormat();
	if (requests.loading) {
		return (
			<Box role="status" sx={{ alignItems: "center", display: "flex", gap: 1, p: 2.5 }}>
				<CircularProgress size={20} />
				{t("fernieChat.requests.loading")}
			</Box>
		);
	}
	if (requests.error) {
		return <RetryableErrorState dense message={t("fernieChat.requests.error")} onRetry={requests.retry} title="" />;
	}
	const rows = [...(requests.payload?.requests ?? [])].sort((a, b) => Date.parse(b.filedAt) - Date.parse(a.filedAt));
	if (rows.length === 0) {
		return (
			<BodyText data-tour-id={TOUR_ANCHORS.fernieRequestsStatus} sx={{ color: "text.secondary", p: 2.5 }}>
				{t("fernieChat.requests.empty")}
			</BodyText>
		);
	}
	return (
		<Box component="ul" sx={{ listStyle: "none", m: 0, p: 0 }}>
			{rows.map((row, index) => (
				<Box component="li" key={row.id} sx={{ borderBottom: 1, borderColor: "divider", px: 2.5, py: 2 }}>
					<Box
						sx={{
							alignItems: "baseline",
							color: "text.secondary",
							display: "flex",
							fontSize: 12,
							gap: 1,
							justifyContent: "space-between"
						}}
					>
						<Box component="span">
							{t(`fernieChat.requests.kind.${row.kind}`, { defaultValue: t("fernieChat.requests.kind.FEATURE") })}
						</Box>
						<Box component="time" dateTime={row.filedAt} sx={{ flexShrink: 0 }}>
							{fmt.formatDate(row.filedAt)}
						</Box>
					</Box>
					<BodyText sx={{ fontSize: 14, fontWeight: 600, mt: 0.75, overflowWrap: "anywhere" }}>{row.title}</BodyText>
					<CaptionText component="p" sx={{ color: "text.secondary", fontSize: 12, mt: 0.75, overflowWrap: "anywhere" }}>
						{t("fernieChat.requests.filedBy", {
							name: row.filedByName?.trim() || row.filedByEmail || t("fernieChat.requests.unknownFiler")
						})}
					</CaptionText>
					<Chip
						color={statusColor[row.status] ?? "default"}
						label={t(`fernieChat.requests.status.${row.status}`, {
							defaultValue: t("fernieChat.requests.status.RECEIVED")
						})}
						data-tour-id={index === 0 ? TOUR_ANCHORS.fernieRequestsStatus : undefined}
						size="small"
						sx={{ borderRadius: "6px", fontSize: 12, height: "auto", mt: 1, py: 0.25 }}
					/>
				</Box>
			))}
		</Box>
	);
};

const FernieRequestsList = ({ requests }: FernieRequestsListProps) => {
	const { t } = useTranslation("common");
	return (
		<Box
			data-tour-id={TOUR_ANCHORS.fernieRequestsList}
			sx={{ display: "flex", flex: 1, flexDirection: "column", minHeight: 0 }}
		>
			<Box sx={{ borderBottom: 1, borderColor: "divider", px: 2.5, py: 2 }}>
				<TitleText component="h1" sx={{ fontSize: 20, fontWeight: 600 }}>
					{t("fernieChat.requests.title")}
				</TitleText>
				<BodyText sx={{ color: "text.secondary", fontSize: 13, mt: 0.5 }}>{t("fernieChat.requests.subtitle")}</BodyText>
			</Box>
			<Box aria-busy={requests.loading} sx={{ flex: 1, overflowY: "auto" }}>
				<FernieRequestsContent requests={requests} />
				{!requests.loading && !requests.error && requests.payload?.truncated && (
					<CaptionText component="p" sx={{ color: "text.secondary", fontSize: 12, p: 2.5 }}>
						{t("fernieChat.requests.truncated")}
					</CaptionText>
				)}
			</Box>
		</Box>
	);
};

export default FernieRequestsList;
